import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { canonical } from '@/lib/site';
import { Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getAllBlogPosts } from '@/data/blogPosts';

export default function BlogIndex() {
  const posts = getAllBlogPosts();

  return (
    <>
      <Helmet>
        <title>Air purifier guides: sizing, smoke, gases, filters and costs</title>
        <meta name="description" content="Plain guides on choosing and sizing an air purifier, wildfire smoke, odours and gases, filters and running costs, with sources." />
        <link rel="canonical" href={canonical('/blog')} />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Air Purifier Blog
            </h1>
            <p className="text-lg text-muted-foreground">
              Practical guides to help you choose, size, and get the most from your air purifier.
            </p>
          </motion.div>

          <div className="space-y-6">
            {posts.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/blog/${post.slug}`} className="group block">
                  <Card className="transition-all duration-200 hover:border-primary/50 hover:shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="secondary">{post.category}</Badge>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {post.readTime} min read
                        </span>
                      </div>
                      <h2 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground mb-3">{post.excerpt}</p>
                      <span className="text-sm font-medium text-primary flex items-center gap-1">
                        Read article
                        <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
